// Plan d'action — Dashboard complet
// Affiche toutes les actions de prévention de l'utilisateur, groupées par diagnostic

import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
// Layout is handled by route-level DashboardLayout
import { useNotifications } from '../contexts/NotificationsContext'
import { Card, CardContent, Button } from '../components/ui'
import { Badge } from '../components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table'
import { Progress } from '../components/ui/progress'
import { Checkbox } from '../components/ui/checkbox'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip'
import { Skeleton } from '../components/ui/skeleton'
import { api } from '../services/api'
import { CasqueIcons } from '../components/results/CasqueDisplay'
import {
  ClipboardList, CheckCircle2, Clock, AlertTriangle,
  ListTodo, ArrowRight, ChevronDown, ExternalLink, Download, Loader2,
} from 'lucide-react'
import { cn } from '../lib/utils'
import { useEntreprise } from '../contexts/EntrepriseContext'

interface ActionPlanRow {
  id: string
  solutionId: string
  diagnosticId: string
  theme: string
  libelle: string
  responsable: string | null
  dateDebut: string | null
  dateFin: string | null
  dateRealisation: string | null
  statut: string
  createdAt: string
  metier: { code: string; nom: string }
  diagnostic: {
    id: string
    parentId: string | null
    versionDiag: number
    createdAt: string
    latestVersionDiag: number
    latestDiagCreatedAt: string
    entreprise: { raisonSociale: string; siret?: string | null }
    resultat: { casques: number } | null
  }
}

type FilterStatut = 'all' | 'a_faire' | 'en_cours' | 'termine' | 'echu'

function isEchu(action: ActionPlanRow): boolean {
  return action.statut !== 'termine' && !!action.dateFin && new Date(action.dateFin) < new Date()
}

function getDisplayStatut(action: ActionPlanRow): string {
  if (action.statut === 'termine') return 'termine'
  if (isEchu(action)) return 'echu'
  return action.statut
}

const STATUT_CONFIG: Record<string, {
  label: string
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
  icon: typeof CheckCircle2
  tooltip: string
}> = {
  a_faire: { label: 'À faire', variant: 'secondary', icon: ListTodo, tooltip: 'Cliquez pour marquer terminé' },
  en_cours: { label: 'En cours', variant: 'default', icon: Clock, tooltip: 'Cliquez pour marquer terminé' },
  termine: { label: 'Terminé', variant: 'outline', icon: CheckCircle2, tooltip: 'Cliquez pour rouvrir' },
  echu: { label: 'Échu', variant: 'destructive', icon: AlertTriangle, tooltip: 'En retard — cliquez pour marquer terminé' },
}

function capitalizeTheme(theme: string): string {
  if (!theme) return theme
  return theme.charAt(0).toUpperCase() + theme.slice(1)
}

// -- Loading skeleton --

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-20 w-full" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

export default function PlanActionDashboardPage() {
  const navigate = useNavigate()
  const { acknowledgePlanAction } = useNotifications()
  const { currentEntreprise, currentSiret } = useEntreprise()
  const [allActions, setAllActions] = useState<ActionPlanRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filterStatut, setFilterStatut] = useState<FilterStatut>('all')
  // Mobile: expanded diagnostic cards
  const [expandedDiags, setExpandedDiags] = useState<Set<string>>(new Set())
  const [solutionLinks, setSolutionLinks] = useState<Map<string, string>>(new Map())
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null)

  // Marquer les notifications plan d'action comme vues
  useEffect(() => {
    acknowledgePlanAction()
  }, [acknowledgePlanAction])

  useEffect(() => {
    api.getAllActionPlans()
      .then((data) => setAllActions(data))
      .catch(console.error)
      .finally(() => setIsLoading(false))

    // Load solutions referentiel for resource links
    fetch('/data/referentiel/solutions.json')
      .then(res => res.ok ? res.json() : null)
      .then(json => {
        if (!json?.data) return
        const links = new Map<string, string>()
        for (const sol of json.data) {
          if (sol.id && sol.lien) links.set(sol.id, sol.lien)
        }
        setSolutionLinks(links)
      })
      .catch(() => { /* ignore — links are optional */ })
  }, [])

  // Filtrer par entreprise courante : SIRET en clé primaire, fallback raisonSociale (legacy null)
  const actions = useMemo(() => {
    if (!currentEntreprise) return allActions
    return allActions.filter(a => {
      const ent = a.diagnostic.entreprise
      if (currentSiret && ent.siret) return ent.siret === currentSiret
      return ent.raisonSociale === currentEntreprise
    })
  }, [allActions, currentEntreprise, currentSiret])

  // Stats KPI
  const stats = useMemo(() => {
    const total = actions.length
    const aFaire = actions.filter(a => a.statut === 'a_faire' && !isEchu(a)).length
    const enCours = actions.filter(a => a.statut === 'en_cours' && !isEchu(a)).length
    const termine = actions.filter(a => a.statut === 'termine').length
    const echu = actions.filter(a => isEchu(a)).length
    return { total, aFaire, enCours, termine, echu }
  }, [actions])

  // Filtrage
  const filtered = useMemo(() => {
    if (filterStatut === 'all') return actions
    if (filterStatut === 'echu') return actions.filter(a => isEchu(a))
    if (filterStatut === 'termine') return actions.filter(a => a.statut === 'termine')
    return actions.filter(a => a.statut === filterStatut && !isEchu(a))
  }, [actions, filterStatut])

  // Grouper par diagnostic racine (consolider les chaînes parent-enfant)
  const byDiagnostic = useMemo(() => {
    const groups = new Map<string, { rootId: string; diagnostic: ActionPlanRow['diagnostic']; actions: ActionPlanRow[] }>()
    for (const action of filtered) {
      const rootId = action.diagnostic.parentId || action.diagnosticId
      if (!groups.has(rootId)) {
        groups.set(rootId, { rootId, diagnostic: action.diagnostic, actions: [] })
      }
      const group = groups.get(rootId)!
      // Garder le diagnostic le plus récent pour l'affichage
      if (new Date(action.diagnostic.createdAt) > new Date(group.diagnostic.createdAt)) {
        group.diagnostic = action.diagnostic
      }
      group.actions.push(action)
    }
    return Array.from(groups.values())
  }, [filtered])

  // Toggle statut inline (optimistic)
  const toggleStatut = async (action: ActionPlanRow) => {
    const newStatut = action.statut === 'termine' ? 'a_faire' : 'termine'
    const newDateRealisation = newStatut === 'termine'
      ? new Date().toISOString().split('T')[0] : null

    setAllActions(prev => prev.map(a =>
      a.id === action.id
        ? { ...a, statut: newStatut, dateRealisation: newDateRealisation }
        : a
    ))

    try {
      await api.patchActionPlan(action.diagnosticId, action.id, {
        statut: newStatut,
        dateRealisation: newDateRealisation,
      })
      toast.success(newStatut === 'termine' ? 'Action terminée' : 'Action réouverte')
    } catch {
      setAllActions(prev => prev.map(a =>
        a.id === action.id ? action : a
      ))
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const toggleDiagExpand = (diagId: string) => {
    setExpandedDiags(prev => {
      const newSet = new Set(prev)
      if (newSet.has(diagId)) newSet.delete(diagId)
      else newSet.add(diagId)
      return newSet
    })
  }

  const handleDownloadPdf = async (diagnosticId: string) => {
    setDownloadingPdf(diagnosticId)
    try {
      await api.downloadPlanActionPDF(diagnosticId)
      toast.success('PDF téléchargé')
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message : 'Erreur lors du téléchargement'
      toast.error(message)
    } finally {
      setDownloadingPdf(null)
    }
  }

  // Compute progress per diagnostic (groupé par racine)
  const diagProgress = useMemo(() => {
    const map = new Map<string, { total: number; termine: number }>()
    for (const action of actions) {
      const key = action.diagnostic.parentId || action.diagnosticId
      if (!map.has(key)) map.set(key, { total: 0, termine: 0 })
      const entry = map.get(key)!
      entry.total++
      if (action.statut === 'termine') entry.termine++
    }
    return map
  }, [actions])

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground tracking-tight">Plan d'action</h1>
        <p className="text-muted-foreground mt-1">Suivez vos actions de prévention</p>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : actions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="size-8 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Aucun plan d'action</h2>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Terminez un diagnostic et s&eacute;lectionnez des solutions pour cr&eacute;er votre premier plan d'action.
            </p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Voir mes diagnostics
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI Cards — clickable to filter */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {([
              { key: 'a_faire' as FilterStatut, count: stats.aFaire, label: 'À faire', icon: ListTodo, iconBg: 'bg-gray-100', iconColor: 'text-gray-600' },
              { key: 'en_cours' as FilterStatut, count: stats.enCours, label: 'En cours', icon: Clock, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
              { key: 'termine' as FilterStatut, count: stats.termine, label: 'Terminées', icon: CheckCircle2, iconBg: 'bg-green-100', iconColor: 'text-green-600' },
              { key: 'echu' as FilterStatut, count: stats.echu, label: 'Échues', icon: AlertTriangle, iconBg: 'bg-red-100', iconColor: 'text-red-600' },
            ]).map(kpi => {
              const KpiIcon = kpi.icon
              return (
                <button
                  key={kpi.key}
                  onClick={() => setFilterStatut(filterStatut === kpi.key ? 'all' : kpi.key)}
                  className={cn(
                    'text-left transition-all rounded-xl border bg-card',
                    filterStatut === kpi.key
                      ? 'ring-2 ring-primary border-primary shadow-sm'
                      : 'border-border hover:border-primary/30 hover:shadow-sm'
                  )}
                >
                  <div className="p-3">
                    <div className="flex items-center gap-3">
                      <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', kpi.iconBg)}>
                        <KpiIcon className={cn('size-5', kpi.iconColor)} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-foreground tabular-nums">{kpi.count}</p>
                        <p className="text-xs text-muted-foreground">{kpi.label}</p>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Tabs filter */}
          <Tabs value={filterStatut} onValueChange={(v) => setFilterStatut(v as FilterStatut)} className="mb-6">
            <TabsList>
              <TabsTrigger value="all">
                Tout <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.total}</span>
              </TabsTrigger>
              <TabsTrigger value="a_faire">
                À faire <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.aFaire}</span>
              </TabsTrigger>
              <TabsTrigger value="en_cours">
                En cours <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.enCours}</span>
              </TabsTrigger>
              <TabsTrigger value="termine">
                Terminé <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.termine}</span>
              </TabsTrigger>
              {stats.echu > 0 && (
                <TabsTrigger value="echu">
                  Échu <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-destructive/15 text-destructive text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.echu}</span>
                </TabsTrigger>
              )}
            </TabsList>

            {['all', 'a_faire', 'en_cours', 'termine', 'echu'].map(tabKey => (
              <TabsContent key={tabKey} value={tabKey} className="mt-4">
                {byDiagnostic.length === 0 ? (
                  <Card>
                    <CardContent className="py-10 text-center">
                      <p className="text-muted-foreground">Aucune action pour ce filtre.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {byDiagnostic.map(({ rootId, diagnostic, actions }) => {
                      const dp = diagProgress.get(rootId) || { total: 0, termine: 0 }
                      const dpPercent = dp.total > 0 ? Math.round((dp.termine / dp.total) * 100) : 0

                      return (
                        <Card key={rootId}>
                          {/* Header diagnostic */}
                          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <h2 className="font-semibold text-foreground truncate">
                                    {diagnostic.entreprise.raisonSociale}
                                  </h2>
                                  {diagnostic.latestVersionDiag > 1 && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                                      V{diagnostic.latestVersionDiag}
                                    </Badge>
                                  )}
                                  {diagnostic.resultat && (
                                    <CasqueIcons count={diagnostic.resultat.casques} size="sm" />
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(diagnostic.createdAt).toLocaleDateString('fr-FR')}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {dp.termine}/{dp.total} terminées
                                  </span>
                                  <div className="w-20">
                                    <Progress value={dpPercent} className="h-1.5" />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-3">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-700 border-blue-300 hover:bg-blue-50 hover:border-blue-400"
                                onClick={() => handleDownloadPdf(rootId)}
                                disabled={downloadingPdf === rootId}
                              >
                                {downloadingPdf === rootId
                                  ? <Loader2 className="size-4 animate-spin" />
                                  : <Download className="size-4 mr-1.5" />
                                }
                                Télécharger le plan d'action
                              </Button>
                              <Button
                                size="sm"
                                className="bg-primary hover:bg-primary/90"
                                onClick={() => navigate(`/dashboard/plan-action/${rootId}`)}
                              >
                                Modifier <ArrowRight className="size-3 ml-1.5" />
                              </Button>
                            </div>
                          </div>

                          {/* Desktop table */}
                          <div className="hidden lg:block">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-[40px] pl-6" />
                                  <TableHead className="w-[120px]">Thème</TableHead>
                                  <TableHead>Action</TableHead>
                                  <TableHead className="w-[100px]">Métier</TableHead>
                                  <TableHead className="w-[120px]">Responsable</TableHead>
                                  <TableHead className="w-[100px]">Échéance</TableHead>
                                  <TableHead className="w-[100px]">Statut</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {actions.map(action => {
                                  const displayStatut = getDisplayStatut(action)
                                  const config = STATUT_CONFIG[displayStatut] || STATUT_CONFIG.a_faire
                                  const StatusIcon = config.icon
                                  const isComplete = action.statut === 'termine'
                                  const isNew = diagnostic.latestVersionDiag > 1 &&
                                    new Date(action.createdAt) > new Date(diagnostic.latestDiagCreatedAt)

                                  return (
                                    <TableRow
                                      key={action.id}
                                      className={cn(
                                        'group transition-colors',
                                        isComplete && 'bg-green-50/40',
                                        displayStatut === 'echu' && 'bg-red-50/30',
                                      )}
                                    >
                                      <TableCell className="pl-6">
                                        <Checkbox
                                          checked={isComplete}
                                          onCheckedChange={() => toggleStatut(action)}
                                          className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="outline" className="text-xs">
                                          {capitalizeTheme(action.theme)}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <span className={cn(
                                          'text-sm font-medium',
                                          isComplete && 'line-through text-muted-foreground'
                                        )}>
                                          {action.libelle}
                                        </span>
                                        {isNew && (
                                          <Badge className="ml-1.5 text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-0">
                                            Nouveau
                                          </Badge>
                                        )}
                                        {solutionLinks.get(action.solutionId) && (
                                          <a
                                            href={solutionLinks.get(action.solutionId)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5"
                                          >
                                            <ExternalLink className="size-3" />
                                            Ressource
                                          </a>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {action.metier.nom}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {action.responsable || '—'}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        <span className={cn(displayStatut === 'echu' && 'text-destructive font-medium')}>
                                          {action.dateFin
                                            ? new Date(action.dateFin).toLocaleDateString('fr-FR')
                                            : '—'
                                          }
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              onClick={() => toggleStatut(action)}
                                              className="inline-flex"
                                            >
                                              <Badge
                                                variant={config.variant}
                                                className={cn(
                                                  'cursor-pointer gap-1 transition-all hover:opacity-80',
                                                  isComplete && 'bg-green-100 text-green-800 border-green-200'
                                                )}
                                              >
                                                <StatusIcon className="size-3" />
                                                {config.label}
                                              </Badge>
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            {config.tooltip}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  )
                                })}
                              </TableBody>
                            </Table>
                          </div>

                          {/* Mobile cards */}
                          <div className="lg:hidden">
                            {/* Toggle expand on mobile */}
                            <button
                              onClick={() => toggleDiagExpand(rootId)}
                              className="w-full px-4 py-2 flex items-center justify-between text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
                            >
                              <span>{actions.length} action{actions.length > 1 ? 's' : ''}</span>
                              <ChevronDown className={cn(
                                'size-4 transition-transform',
                                expandedDiags.has(rootId) && 'rotate-180'
                              )} />
                            </button>
                            {expandedDiags.has(rootId) && (
                              <div className="px-4 pb-4 space-y-2">
                                {actions.map(action => {
                                  const displayStatut = getDisplayStatut(action)
                                  const config = STATUT_CONFIG[displayStatut] || STATUT_CONFIG.a_faire
                                  const StatusIcon = config.icon
                                  const isComplete = action.statut === 'termine'
                                  const isNewMobile = diagnostic.latestVersionDiag > 1 &&
                                    new Date(action.createdAt) > new Date(diagnostic.latestDiagCreatedAt)

                                  return (
                                    <div
                                      key={action.id}
                                      className={cn(
                                        'flex items-start gap-3 p-3 rounded-lg border transition-colors',
                                        isComplete && 'bg-green-50/40 border-green-200',
                                        displayStatut === 'echu' && 'bg-red-50/30 border-red-200',
                                        !isComplete && displayStatut !== 'echu' && 'border-border',
                                      )}
                                    >
                                      <Checkbox
                                        checked={isComplete}
                                        onCheckedChange={() => toggleStatut(action)}
                                        className="mt-0.5 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className={cn(
                                          'text-sm font-medium',
                                          isComplete && 'line-through text-muted-foreground'
                                        )}>
                                          {action.libelle}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                          {isNewMobile && (
                                            <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-0">
                                              Nouveau
                                            </Badge>
                                          )}
                                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                            {capitalizeTheme(action.theme)}
                                          </Badge>
                                          <Badge variant={config.variant} className="gap-1 text-[10px] px-1.5 py-0">
                                            <StatusIcon className="size-2.5" />
                                            {config.label}
                                          </Badge>
                                          {action.dateFin && (
                                            <span className={cn(
                                              'text-[10px] text-muted-foreground',
                                              displayStatut === 'echu' && 'text-destructive font-medium'
                                            )}>
                                              Éch. {new Date(action.dateFin).toLocaleDateString('fr-FR')}
                                            </span>
                                          )}
                                          {solutionLinks.get(action.solutionId) && (
                                            <a
                                              href={solutionLinks.get(action.solutionId)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
                                            >
                                              <ExternalLink className="size-2.5" />
                                              Ressource
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </Card>
                      )
                    })}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </>
  )
}
